/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";
import { setActiveSupportMode } from "@/integrations/supabase/support-mode-ref";
import { toast } from "sonner";
import { useTenantContext } from "./useTenantContext";
import type { TenantContext } from "@/lib/tenant-context";
import { logPlatformEvent } from "@/lib/platform-event-logger";

interface SupportModeState {
  active: boolean;
  tenantContext: TenantContext | null;
  enteredAt: string | null;
}

interface SupportModeContextType {
  supportMode: SupportModeState;
  enterSupportMode: (orgId: string, orgName: string, reason: string) => Promise<void>;
  exitSupportMode: () => Promise<void>;
}

const SupportModeContext = createContext<SupportModeContextType | undefined>(undefined);

export function SupportModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { overrideContext, setOverrideContext, clearOverrideContext } = useTenantContext();
  const [enteredAt, setEnteredAt] = useState<string | null>(null);
  const supportTenantContext = overrideContext?.source === "support" ? overrideContext : null;

  const resetTenantQueries = useCallback(async () => {
    // The previous version only reset queries whose key starts with
    // ["tenant"]. Many tenant-scoped caches use other prefixes
    // (["jobs"], ["people"], ["admin", "organization", orgId],
    // ["volunteer-assignments"], etc.). Those caches survived support-
    // mode entry/exit, so admins would briefly see stale data from the
    // previous tenant after switching context. Reset every query —
    // belt-and-suspenders for what is already a low-frequency action.
    // Active queries are refetched immediately so the UI doesn't flash
    // empty.
    await queryClient.resetQueries();
    await queryClient.refetchQueries({ type: "active" });
  }, [queryClient]);

  const createSupportSession = useCallback(
    async (orgId: string, reason: string) => {
      if (!user) return null;
      // Use the SECURITY DEFINER RPC `create_support_session` rather than a
      // direct insert. The previous direct insert was broken in three ways:
      //   1. Column names were wrong (`actor_user_id`/`organization_id`/`reason`
      //      vs. the actual schema's `platform_user_id`/`target_org_id`/
      //      `reason_detail`).
      //   2. Required NOT NULL columns `mode` and `expires_at` were never
      //      sent, so even with corrected column names the insert would have
      //      failed with a constraint violation.
      //   3. The RLS WITH CHECK clause requires `platform_user_id = auth.uid()`
      //      anyway. The RPC pins the actor server-side, so a forged client
      //      payload can't attribute the session to another admin.
      // Net effect: support mode could never have been entered successfully
      // from this hook in its prior form.
      //
      // Default support-session lifetime: 1 hour. The admin layout countdown
      // auto-exits support mode when this deadline passes.
      const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase.rpc("create_support_session", {
        target_org_id: orgId,
        mode: "support",
        reason_code: "admin_support",
        reason_detail: reason,
        expires_at: expiresAt,
      });

      if (error) {
        throw error;
      }

      const sessionRow = data as { id?: string } | null;
      // Return the expiry alongside the id so callers can propagate it
      // into TenantContext for the live banner countdown. Without this,
      // the banner would have to round-trip to support_sessions for
      // every render — wasteful for what is effectively a static
      // wall-clock deadline.
      return sessionRow?.id ? { id: sessionRow.id, expiresAt } : null;
    },
    [user],
  );

  const logSupportModeEvent = useCallback(
    async (eventType: "support_mode_entered" | "support_mode_exited", details: Record<string, unknown>) => {
      const organizationId = typeof details.organization_id === "string" ? details.organization_id : null;
      const reason = typeof details.reason === "string" ? details.reason : null;

      try {
        await logPlatformEvent({
          category: "organization",
          eventType,
          status: "success",
          userId: user?.id ?? null,
          message: eventType === "support_mode_entered" ? "Support mode entered" : "Support mode exited",
          metadata: {
            actor_id: user?.id ?? null,
            target_id: organizationId,
            reason,
            org_id: organizationId,
            ...details,
          },
          source: "support-mode",
        });
      } catch (error) {
        console.warn("Failed to log support mode event:", error);
      }
    },
    [user],
  );

  const endSupportSession = useCallback(
    async (supportSessionId: string) => {
      if (!user) return;
      // Use the SECURITY DEFINER RPC `end_support_session`. The previous
      // direct UPDATE relied on the table's RLS WITH CHECK clause; under the
      // current policy that's fine for self-owned sessions, but the RPC also
      // ensures the row actually existed (raises "Support session not found
      // or already ended") rather than silently no-op'ing on a missing or
      // already-closed session — which would otherwise leave the audit view
      // reporting the session as Active forever.
      const { error } = await supabase.rpc("end_support_session", {
        support_session_id: supportSessionId,
      });

      if (error) {
        throw error;
      }
    },
    [user],
  );

  const enterSupportMode = useCallback(async (orgId: string, orgName: string, reason: string) => {
    if (!user) return;

    let newSession: { id: string; expiresAt: string } | null = null;
    try {
      // If admin is already in a support session for a different org, end
      // the old one FIRST and write its exit audit row. Previously the
      // order was: create new → log enter for new → end old. That left
      // the old session with no `exit_support_mode` audit row, so the
      // audit timeline showed Org A ending silently and Org B starting,
      // with no event marking the transition. End-before-create produces
      // a clean enter/exit pair per session.
      const previousSession = supportTenantContext;
      if (previousSession?.supportSessionId) {
        try {
          await endSupportSession(previousSession.supportSessionId);
          await supabase.from("admin_audit_logs").insert({
            actor_user_id: user.id,
            target_type: "organization",
            target_id: previousSession.organizationId,
            action: "exit_support_mode",
            reason: "switched_to_other_org",
            organization_id: previousSession.organizationId,
            metadata: {
              organization_name: previousSession.organizationName,
              support_session_id: previousSession.supportSessionId,
              switched_to_org_id: orgId,
              switched_to_org_name: orgName,
            },
          });
          await logSupportModeEvent("support_mode_exited", {
            organization_id: previousSession.organizationId,
            organization_name: previousSession.organizationName,
            support_session_id: previousSession.supportSessionId,
            reason: "switched_to_other_org",
            switched_to_org_id: orgId,
          });
        } catch (switchExitError) {
          // Don't block entering the new session if exit-logging fails;
          // surface the warning and continue. The new-session create
          // below is the user-visible operation.
          console.warn(
            "[useSupportMode] failed to write exit audit for previous session:",
            switchExitError,
          );
        }
      }

      newSession = await createSupportSession(orgId, reason);

      if (!newSession) {
        throw new Error("Failed to create support session.");
      }

      const { error } = await supabase
        .from("admin_audit_logs")
        .insert({
          actor_user_id: user.id,
          target_type: "organization",
          target_id: orgId,
          action: "enter_support_mode",
          reason,
          organization_id: orgId,
          metadata: {
            organization_name: orgName,
            support_session_id: newSession.id,
            expires_at: newSession.expiresAt,
          },
        });

      if (error) throw error;

      await logSupportModeEvent("support_mode_entered", {
        organization_id: orgId,
        organization_name: orgName,
        support_session_id: newSession.id,
        expires_at: newSession.expiresAt,
        reason,
      });

      setOverrideContext({
        organizationId: orgId,
        organizationName: orgName,
        supportSessionId: newSession.id,
        // Propagate the wall-clock expiry into TenantContext so the
        // banner can render a live countdown without a per-render
        // round-trip to support_sessions.
        supportSessionExpiresAt: newSession.expiresAt,
        source: "support",
      });
      setEnteredAt(new Date().toISOString());
      await resetTenantQueries();

      // Additional audit log already handled above
      toast.success("Entered support mode", {
        description: `Now viewing ${orgName}`,
      });
    } catch (error) {
      if (newSession) {
        try {
          await endSupportSession(newSession.id);
        } catch (cleanupError) {
          console.error("Failed to clean up support session:", cleanupError);
        }
      }
      console.error("Failed to enter support mode:", error);
      toast.error("Failed to enter support mode");
    }
  }, [createSupportSession, endSupportSession, logSupportModeEvent, resetTenantQueries, setOverrideContext, supportTenantContext, user]);

  const exitSupportMode = useCallback(async () => {
    if (!user || !supportTenantContext) return;

    try {
      if (supportTenantContext.supportSessionId) {
        await endSupportSession(supportTenantContext.supportSessionId);
      }

      const { error } = await supabase
        .from("admin_audit_logs")
        .insert({
          actor_user_id: user.id,
          target_type: "organization",
          target_id: supportTenantContext.organizationId,
          action: "exit_support_mode",
          organization_id: supportTenantContext.organizationId,
          metadata: {
            organization_name: supportTenantContext.organizationName,
            support_session_id: supportTenantContext.supportSessionId ?? null,
            duration_seconds: enteredAt
              ? Math.floor((Date.now() - new Date(enteredAt).getTime()) / 1000)
              : null,
          },
        });

      if (error) throw error;

      await logSupportModeEvent("support_mode_exited", {
        organization_id: supportTenantContext.organizationId,
        organization_name: supportTenantContext.organizationName,
        support_session_id: supportTenantContext.supportSessionId ?? null,
        duration_seconds: enteredAt
          ? Math.floor((Date.now() - new Date(enteredAt).getTime()) / 1000)
          : null,
        reason: "exit_support_mode",
      });

      clearOverrideContext();
      setEnteredAt(null);
      await resetTenantQueries();

      toast.info("Exited support mode");
    } catch (error) {
      console.error("Failed to exit support mode:", error);
      toast.error("Failed to exit support mode");
    }
  }, [clearOverrideContext, endSupportSession, enteredAt, logSupportModeEvent, resetTenantQueries, supportTenantContext, user]);

  const supportMode: SupportModeState = {
    active: Boolean(supportTenantContext),
    tenantContext: supportTenantContext,
    enteredAt,
  };

  // Sync the module-level ref consumed by the supabase fetch wrapper.
  // The wrapper lives outside the React tree (it's set up once at boot
  // in `integrations/supabase/client.ts`), so it can't read this context
  // directly — instead it polls a module-level ref that we update here
  // on every transition. See `support-mode-ref.ts` for why a singleton
  // is the right shape for this signal.
  useEffect(() => {
    if (
      supportTenantContext &&
      supportTenantContext.supportSessionId &&
      user?.id
    ) {
      setActiveSupportMode({
        sessionId: supportTenantContext.supportSessionId,
        targetOrgId: supportTenantContext.organizationId,
        adminUserId: user.id,
      });
    } else {
      setActiveSupportMode(null);
    }
    return () => {
      // On unmount (provider tear-down e.g. signOut), clear the ref so a
      // subsequent mount doesn't inherit a stale session id.
      setActiveSupportMode(null);
    };
  }, [
    supportTenantContext?.supportSessionId,
    supportTenantContext?.organizationId,
    user?.id,
    supportTenantContext,
  ]);

  return (
    <SupportModeContext.Provider value={{ supportMode, enterSupportMode, exitSupportMode }}>
      {children}
    </SupportModeContext.Provider>
  );
}

export function useSupportMode() {
  const context = useContext(SupportModeContext);
  if (context === undefined) {
    throw new Error("useSupportMode must be used within a SupportModeProvider");
  }
  return context;
}
